import React, { useMemo, useState } from "react";
import { TelegramDissolveRemoval } from "./telegramDissolve";

function DemoCard({ tone = "neutral", removed = false, title, teacher, room, type, subtitle }) {
  const toneClass =
    tone === "next"
      ? "border-blue-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(239,246,255,0.96))]"
      : "border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(247,250,253,0.96))]";
  const removalClass = removed ? "schedule-lesson-card--removed" : "";

  return (
    <div
      className={`schedule-lesson-card ${removalClass} space-y-2.5 rounded-[22px] border ${toneClass} p-3.5 shadow-[0_18px_42px_-32px_rgba(15,23,42,0.28)]`}
    >
      <div className="font-semibold break-words text-[15px] leading-[1.18] tracking-[-0.01em] text-slate-800">
        {title}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-[5px] text-[10px] font-semibold text-emerald-700 shadow-[0_8px_18px_-14px_rgba(16,185,129,0.45)]">
          {type}
        </span>
        {subtitle && !removed ? (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-[5px] text-[10px] font-semibold text-slate-700">
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="flex items-start gap-2 text-[12px] font-medium text-slate-600">
        <span className="mt-[2px] h-4 w-4 shrink-0 text-blue-500">•</span>
        <span className="break-words leading-[1.35]">{teacher}</span>
      </div>
      <div className="flex items-start gap-2 text-[12px] font-medium text-slate-600">
        <span className="mt-[2px] h-4 w-4 shrink-0 text-rose-500">•</span>
        <span className="break-words leading-[1.35]">{room}</span>
      </div>
    </div>
  );
}

function EmptySlot() {
  return (
    <div className="flex h-full min-h-[208px] items-center justify-center rounded-[22px] border border-dashed border-slate-200 text-sm font-medium text-slate-400">
      Карточка удалена
    </div>
  );
}

export default function TelegramDissolveLab() {
  const [runKey, setRunKey] = useState(0);
  const [mode, setMode] = useState("replace");
  const [showRemoved, setShowRemoved] = useState(true);
  const [showNext, setShowNext] = useState(false);
  const [lastRunMode, setLastRunMode] = useState("replace");
  const [engine, setEngine] = useState("pending");
  const [engineError, setEngineError] = useState("");
  const [completedRemovals, setCompletedRemovals] = useState(0);

  const currentCards = useMemo(
    () => [
      {
        title: "Анализ данных",
        teacher: "Салихов Р.Н.",
        room: "ауд. 214",
        type: "Лекция",
        subtitle: "1 пара",
      },
      {
        title: "Технологии web-программирования",
        teacher: "Мохова С.А.",
        room: "ауд. 111",
        type: "Практика",
        subtitle: "3 пара",
      },
      {
        title: "Базы данных",
        teacher: "Кузнецов А.В.",
        room: "лаб. 305",
        type: "Лаба",
        subtitle: "2 пара",
      },
      {
        title: "UI/UX проектирование",
        teacher: "Хайбуллина Е.М.",
        room: "ауд. 118",
        type: "Практика",
        subtitle: "4 пара",
      },
    ],
    []
  );

  const nextCards = useMemo(
    () => [
      {
        title: "Статистика и прогнозирование",
        teacher: "Салихов Р.Н.",
        room: "ауд. 214",
        type: "Лекция",
        subtitle: "Обновлено",
      },
      {
        title: "Разработка SPA-приложений",
        teacher: "Мохова С.А.",
        room: "ауд. 111",
        type: "Практика",
        subtitle: "Новая пара",
      },
      {
        title: "NoSQL и хранилища данных",
        teacher: "Кузнецов А.В.",
        room: "лаб. 305",
        type: "Лаба",
        subtitle: "Обновлено",
      },
      {
        title: "Продуктовый дизайн",
        teacher: "Хайбуллина Е.М.",
        room: "ауд. 118",
        type: "Практика",
        subtitle: "Обновлено",
      },
    ],
    []
  );

  const totalCards = currentCards.length;

  const startRun = (nextMode) => {
    setMode(nextMode);
    setLastRunMode(nextMode);
    setRunKey((value) => value + 1);
    setEngine("pending");
    setEngineError("");
    setCompletedRemovals(0);
    setShowRemoved(true);
    setShowNext(false);
  };

  const handleRemovalComplete = () => {
    setCompletedRemovals((value) => {
      const nextValue = value + 1;
      if (nextValue >= totalCards) {
        setShowRemoved(false);
        if (mode === "replace") {
          setShowNext(true);
        }
      }
      return nextValue;
    });
  };

  return (
    <section className="mx-auto max-w-5xl space-y-8 px-4 py-10 sm:px-6">
      <div className="space-y-3">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold tracking-[0.18em] text-slate-500 uppercase">
          Telegram Dissolve Lab
        </div>
        <h1 className="text-3xl font-semibold tracking-[-0.03em] text-slate-900">
          Экспериментальная площадка анимации удаления
        </h1>
        <p className="max-w-2xl text-sm leading-6 text-slate-600">
          Здесь dissolve проигрывается сразу для всей мини-сетки пар, чтобы можно было смотреть
          эффект в более реалистичном контексте.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => startRun("remove")}
          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
        >
          Проиграть удаление
        </button>
        <button
          type="button"
          onClick={() => startRun("replace")}
          className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
        >
          Проиграть удаление и замену
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="rounded-[28px] border border-slate-200/80 bg-white/90 p-4 shadow-[0_24px_60px_-34px_rgba(15,23,42,0.28)]">
          <div className="mb-4 text-sm font-semibold text-slate-900">Превью карточек</div>
          <div className="relative overflow-hidden rounded-[24px] border border-slate-800/80 bg-[radial-gradient(circle_at_top,rgba(71,85,105,0.34),transparent_44%),linear-gradient(180deg,rgba(15,23,42,0.96),rgba(30,41,59,0.94))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div className="pointer-events-none absolute inset-[18px] rounded-[20px] bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.12),rgba(255,255,255,0)_72%)]" />

            <div className="relative grid gap-3 sm:grid-cols-2">
              {currentCards.map((card, index) => (
                <div key={`slot-${runKey}-${index}`} className="relative min-h-[208px]">
                  {showNext ? (
                    <div className="animate-[scheduleLessonAddedIn_640ms_cubic-bezier(0.2,0.88,0.28,1)_both]">
                      <DemoCard
                        tone="next"
                        title={nextCards[index].title}
                        teacher={nextCards[index].teacher}
                        room={nextCards[index].room}
                        type={nextCards[index].type}
                        subtitle={nextCards[index].subtitle}
                      />
                    </div>
                  ) : null}

                  {showRemoved ? (
                    <TelegramDissolveRemoval
                      key={`lab-${runKey}-${index}`}
                      className={`absolute inset-0 ${showNext ? "pointer-events-none" : ""}`}
                      duration={4000}
                      introHoldDuration={400}
                      lingerDuration={0}
                      startDelay={index * 90}
                      onEngineChange={(nextEngine, error) => {
                        setEngine(nextEngine);
                        setEngineError(error ? String(error.message || error) : "");
                      }}
                      onComplete={handleRemovalComplete}
                    >
                      <DemoCard
                        removed
                        title={card.title}
                        teacher={card.teacher}
                        room={card.room}
                        type={card.type}
                        subtitle={card.subtitle}
                      />
                    </TelegramDissolveRemoval>
                  ) : null}

                  {!showRemoved && !showNext ? <EmptySlot /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-[28px] border border-slate-200/80 bg-white/90 p-5 shadow-[0_24px_60px_-34px_rgba(15,23,42,0.28)]">
          <div>
            <div className="text-sm font-semibold text-slate-900">Как открыть</div>
            <div className="mt-2 text-sm leading-6 text-slate-600">
              Открой в браузере маршрут <code>/labs/telegram-dissolve</code>.
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Что смотреть</div>
            <div className="mt-2 text-sm leading-6 text-slate-600">
              Теперь анимация играет сразу на всей сетке. В режиме замены весь старый набор пар
              уходит и после этого появляется новый.
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Текущий режим</div>
            <div className="mt-2 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              {lastRunMode === "replace" ? "Удаление + замена" : "Только удаление"}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Прогресс удаления</div>
            <div className="mt-2 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              {completedRemovals}/{totalCards}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Движок анимации</div>
            <div className="mt-2 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              {engine}
            </div>
            {engineError ? (
              <div className="mt-2 text-xs leading-5 text-rose-600">{engineError}</div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
